<template>
  <div class="guided-mode__summary">
    <p class="guided-mode__summary-intro">{{ $t('app.assessment_mode.guided.summary.review_prompt') }}</p>
    <section class="guided-mode__card guided-mode__summary-section">
      <h2 class="guided-mode__summary-heading">{{ $t('app.summary.patient_demographics.title') }}</h2>
      <div v-for="item in demographics" :key="item.id" class="guided-mode__summary-row">
        <div class="guided-mode__summary-content">
          <strong class="guided-mode__summary-title">{{ item.label }}</strong>
          <p class="guided-mode__summary-text">{{ item.value }}</p>
        </div>
        <button v-if="!item.readOnly" type="button" data-analytics-skip class="guided-mode__demographics-edit" :aria-label="$t('app.assessment_mode.text.input.edit_observation', { name: item.label })" :title="$t('app.assessment_mode.text.input.edit_observation', { name: item.label })" @click="$emit('edit-demographics')">
          <svg class="guided-mode__edit-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="mdiPencilOutline" />
          </svg>
        </button>
      </div>
    </section>
    <section class="guided-mode__card guided-mode__summary-section">
      <h2 class="guided-mode__summary-heading">{{ $t('app.summary.observations.title') }}</h2>
      <div class="guided-mode__summary-row">
        <div class="guided-mode__summary-content">
          <strong class="guided-mode__summary-title">{{ symptomsLabel }}</strong>
          <p v-if="symptoms.length" class="guided-mode__summary-text">{{ symptomsText }}</p>
          <p
            v-if="isComprehensiveScope"
            class="guided-mode__summary-text guided-mode__assessment-scope"
          >
            {{ $t('app.assessment_mode.summary.full_systems_assessment') }}
          </p>
        </div>
        <button type="button" data-analytics-skip class="guided-mode__symptoms-edit" :aria-label="$t('app.assessment_mode.text.input.edit_observation', { name: symptomsLabel })" :title="$t('app.assessment_mode.text.input.edit_observation', { name: symptomsLabel })" @click="$emit('edit-symptoms')">
          <svg class="guided-mode__edit-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="mdiPencilOutline" />
          </svg>
        </button>
      </div>
      <div v-for="item in summaryItems" :key="Array.isArray(item) ? item[0].sourceGroupId : item.question.id" class="guided-mode__summary-row">
        <div v-if="Array.isArray(item)" class="guided-mode__summary-content">
          <strong class="guided-mode__summary-title">
            {{ $t(getGroupTitleToken(item[0].sourceGroupId, item[0].groupTitle)) }}
          </strong>
          <div v-for="answer in item" :key="answer.question.id" class="guided-mode__summary-group-item">
            <p class="guided-mode__summary-text">{{ answer.title }} :  {{ answer.answer }}</p>
            <button
              type="button"
              data-analytics-skip
              class="guided-mode__edit"
              :aria-label="$t('app.assessment_mode.guided.summary.edit_answer')"
              :title="$t('app.assessment_mode.guided.summary.edit_answer')"
              @click="$emit('edit', answer.stepIndex)"
            >
              <svg class="guided-mode__edit-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path :d="mdiPencilOutline" />
              </svg>
            </button>
          </div>
        </div>
        <div v-else class="guided-mode__summary-content">
          <strong class="guided-mode__summary-title">{{ item.title }}</strong>
          <p class="guided-mode__summary-text">{{ item.answer }}</p>
        </div>
        <button
            v-if="!Array.isArray(item)"
            type="button"
            data-analytics-skip
            class="guided-mode__edit"
            :aria-label="$t('app.assessment_mode.guided.summary.edit_answer')"
            :title="$t('app.assessment_mode.guided.summary.edit_answer')"
            @click="$emit('edit', item.stepIndex)"
        >
          <svg class="guided-mode__edit-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="mdiPencilOutline" />
          </svg>
        </button>
      </div>
    </section>
    <div v-if="allowCopyPaste" class="guided-mode__summary-copy">
      <copy-clipboard-button
        token="app.assessment.summary.button.copy.title"
        name="summary"
        css-class="copy-all-btn"
        :payload="getCopyPayload"
        mode="button"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mdiPencilOutline } from '@mdi/js'
import { mapState } from 'pinia'
import CopyClipboardButton from '@/components/widgets/CopyClipboardButton.vue'
import { usePartnerConfigStore } from '@/stores/partnerConfig'
import { useTrackStore } from '@/stores/track'
import { buildGuidedSummaryCopyPayload } from '@/utils/encounterCopyUtils'
import type { GuidedModeAnsweredItem, GuidedModeDemographicItem } from '../types/guided-mode.types'

export default defineComponent({
  name: 'GuidedModeSummary',
  components: { CopyClipboardButton },
  props: {
    demographics: {
      type: Array as PropType<GuidedModeDemographicItem[]>,
      default: () => []
    },
    items: {
      type: Array as PropType<GuidedModeAnsweredItem[]>,
      required: true
    },
    symptoms: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },
  emits: ['edit', 'edit-demographics', 'edit-symptoms'],
  data: () => ({ mdiPencilOutline }),
  computed: {
    ...mapState(usePartnerConfigStore, ['allowCopyPaste']),
    ...mapState(useTrackStore, ['isComprehensiveScope']),
    symptomsLabel(): string {
      return String(this.$t('app.assessment_mode.guided.symptoms.title'))
    },
    symptomsText(): string {
      return this.symptoms.join(' · ')
    },
    additionalSymptomsLabel(): string {
      return String(this.$t('app.assessment_mode.guided.additional_symptoms.title'))
    },
    additionalSymptomsText(): string {
      return (this.additionalSymptoms || []).join(' · ')
    },
    summaryItems(): (GuidedModeAnsweredItem | GuidedModeAnsweredItem[])[] {
      const items = this.items
      const len = items.length
      if (len === 0) return []

      const translatedGroupId = (item: GuidedModeAnsweredItem): string | undefined => {
        const groupId = item.sourceGroupId
        if (!groupId) return undefined
        const token = this.getGroupTitleToken(groupId, item.groupTitle)
        return token && this.$t(token) !== token ? groupId : undefined
      }
      const grouped = new Map<string, GuidedModeAnsweredItem[]>()
      for (let i = 0; i < len; i++) {
        const item = items[i]
        const groupId = translatedGroupId(item)
        if (groupId === undefined) continue
        const bucket = grouped.get(groupId)
        if (bucket) {
          bucket.push(item)
        } else {
          grouped.set(groupId, [item])
        }
      }

      // Second pass: rebuild the result list preserving the original
      // order. When we hit the first item of a group, push the full
      // array; subsequent items from the same group are skipped.
      const result: (GuidedModeAnsweredItem | GuidedModeAnsweredItem[])[] = []
      const emittedGroups = new Set<string>()
      for (let i = 0; i < len; i++) {
        const item = items[i]
        const groupId = translatedGroupId(item)
        if (groupId === undefined) {
          // Standalone item -> single element in the result array
          result.push(item)
          continue
        }
        if (emittedGroups.has(groupId)) continue
        emittedGroups.add(groupId)
        // Items sharing the same sourceGroupId go into one array element
        result.push(grouped.get(groupId)!)
      }

      return result
    },
  },
  methods: {
    getGroupTitleToken(sourceGroupId: string | undefined, fallback: string | undefined): string {
      if (sourceGroupId && sourceGroupId.startsWith('group.')) {
        const token = `questions.${sourceGroupId.slice('group.'.length)}.title`
        if (this.$t(token) !== token) return token
      }
      return fallback || ''
    },
    getCopyPayload(): string {
      const identifiers = this.demographics
        .filter(item => item.id === 'identifier')
        .map(item => ({ label: item.label, value: item.value }))

      return buildGuidedSummaryCopyPayload({
        identifiers,
        demographics: this.demographics,
        symptoms: { label: this.symptomsLabel, value: this.symptomsText },
        items: this.items,
        translate: token => String(this.$t(token)),
        rtl: this.$vuetify.rtl,
      })
    },
  },
})
</script>
